import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FieldCard from '../../components/FieldCard';
import { fields, districts } from '../../data/mockData';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [district, setDistrict] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [nearbyError, setNearbyError] = useState('');

  const featuredFields = fields.slice(0, 4);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (district) params.set('district', district);
    if (date) params.set('date', date);
    if (time) params.set('time', time);
    navigate(`/sahalar?${params.toString()}`);
  };

  const handleFindNearby = () => {
    if (!navigator.geolocation) {
      setNearbyError('Tarayıcınız konum özelliğini desteklemiyor.');
      return;
    }

    setNearbyError('');
    setNearbyLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const params = new URLSearchParams();
        params.set('nearby', '1');
        params.set('lat', String(position.coords.latitude));
        params.set('lng', String(position.coords.longitude));
        if (date) params.set('date', date);
        if (time) params.set('time', time);
        navigate(`/sahalar?${params.toString()}`);
      },
      () => {
        setNearbyError('Konum izni verilmediği için yakın sahalar gösterilemedi.');
        setNearbyLoading(false);
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&w=2400&q=80"
            alt="Halı saha arka plan"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/45" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/20 to-slate-900/60" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Maç saati geldi, saha hazır!
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              İstediğin saatte istediğin yerde maçını bul hızlıca rezerve et
            </p>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-4 md:p-6 shadow-2xl">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-gray-600 text-sm mb-2">Konum</label>
                  <select
                    value={district}
                    onChange={(e) => setDistrict(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:outline-none focus:border-primary-500 transition-colors"
                  >
                    <option value="">Tüm İlçeler</option>
                    {districts.map((d) => (
                      <option key={d} value={d}>İstanbul / {d}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-600 text-sm mb-2">Tarih</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:outline-none focus:border-primary-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-gray-600 text-sm mb-2">Saat</label>
                  <select
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-3 text-gray-900 focus:outline-none focus:border-primary-500 transition-colors"
                  >
                    <option value="">Saat Seçin</option>
                    {Array.from({ length: 17 }, (_, i) => i + 7).map((h) => (
                      <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    type="submit"
                    className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span>Ara</span>
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-4 flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={handleFindNearby}
                disabled={nearbyLoading}
                className="inline-flex items-center gap-2 rounded-lg bg-white/90 text-gray-900 px-4 py-2.5 border border-white hover:bg-white transition-colors disabled:opacity-60"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 01.553-.894L9 2m0 18l6-3m-6 3V2m6 15l5.447 2.724A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 2" />
                </svg>
                <span>{nearbyLoading ? 'Konum Alınıyor...' : 'Yakınımdaki Sahaları Bul'}</span>
              </button>
              {nearbyError && <p className="text-xs text-rose-200">{nearbyError}</p>}
            </div>
          </form>

        </div>
      </section>

      {/* Featured Fields */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Öne Çıkan Sahalar</h2>
              <p className="text-gray-500 mt-2">En popüler ve en çok tercih edilen sahalar</p>
            </div>
            <button
              onClick={() => navigate('/sahalar')}
              className="hidden md:flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors"
            >
              <span>Tümünü Gör</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredFields.map((field) => (
              <FieldCard key={field.id} field={field} />
            ))}
          </div>

          <div className="mt-8 text-center md:hidden">
            <button
              onClick={() => navigate('/sahalar')}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Tümünü Gör &rarr;
            </button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Nasıl Çalışır?</h2>
            <p className="text-gray-500 mt-2">3 kolay adımda halı saha rezervasyonunuzu yapın</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-gray-900 text-lg font-semibold mb-2">1. Saha Bul</h3>
              <p className="text-gray-500 text-sm">
                Konumunuza ve tercihlerinize göre en uygun halı sahayı bulun. Filtreleme seçenekleriyle aradığınızı kolayca bulun.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-gray-900 text-lg font-semibold mb-2">2. Tarih & Saat Seç</h3>
              <p className="text-gray-500 text-sm">
                Uygun tarih ve saat dilimini seçin. Anlık müsaitlik durumunu takvim üzerinden kontrol edin.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-gray-900 text-lg font-semibold mb-2">3. Rezervasyon Yap</h3>
              <p className="text-gray-500 text-sm">
                Takım bilgilerinizi girin ve rezervasyonunuzu onaylayın. Sahaya gitmeye hazırsınız!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Match Planning Section */}
      <section className="bg-white py-16 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Maç Planlama Köşesi</h2>
            <p className="text-gray-500 mt-2">Daha kolay rezervasyon için kısa bilgiler</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="rounded-xl border border-sky-200 bg-sky-50 p-5">
              <p className="text-sm font-semibold text-sky-900">Popüler Saatler</p>
              <p className="text-xs text-sky-800 mt-2">18:00 - 22:00 arası daha hızlı doluyor. Erken saatlerde fiyatlar genelde daha uygun.</p>
              <button
                onClick={() => navigate('/sahalar')}
                className="mt-4 text-xs font-medium rounded-md border border-sky-300 bg-white text-sky-800 px-3 py-1.5 hover:bg-sky-100"
              >
                Sahaları İncele
              </button>
            </div>

            <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
              <p className="text-sm font-semibold text-amber-900">Maç Öncesi Kontrol</p>
              <p className="text-xs text-amber-800 mt-2">Krampon kiralama, sıcak su ve kafetarya bilgisini rezervasyon öncesi mutlaka kontrol et.</p>
              <button
                onClick={() => navigate('/sahalar')}
                className="mt-4 text-xs font-medium rounded-md border border-amber-300 bg-white text-amber-800 px-3 py-1.5 hover:bg-amber-100"
              >
                Filtrelerle Ara
              </button>
            </div>

            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
              <p className="text-sm font-semibold text-emerald-900">Abonelik Avantajı</p>
              <p className="text-xs text-emerald-800 mt-2">Aynı gün ve saatte düzenli maç yapıyorsan abonelikle slotunu sürekli sabitleyebilirsin.</p>
              <button
                onClick={() => navigate('/profil')}
                className="mt-4 text-xs font-medium rounded-md border border-emerald-300 bg-white text-emerald-800 px-3 py-1.5 hover:bg-emerald-100"
              >
                Profilime Git
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
